import { useEffect, useState } from 'react';
import 'material-symbols';

import { useDispatch, useSelector } from 'react-redux';
import { setFeatureSetList, setRouteList } from './pages/configuration/routeSlice';
import RoutesContainer from './routes/RoutesContainer';
import AuthService from './services/authService';

import {
  AbpApplicationConfigurationService,
  CompanyFeatureService,
} from './services/wealthlane-identity-services';

import Navbar from './components/navbar';
import Spinners from './components/spinner';

import { InnerContainer } from './constants/style/common.styled';
import { MainContentWrapper } from './components/navbar/navbar.styled';
import pingBg from '../src/assets/images/pink_background.png';
import { getFilteredPermissionData } from './utils';

import { selectToken, setCredentials } from './pages/auth/authSlice';
import { selectNotify } from './pages/auth/notify';
import { useLocation, useNavigate } from 'react-router-dom';
import useWealthlaneIdentityApi from './hooks/useWealthlaneIdentityApi';
import { useBoolean } from './hooks';
import AlertPopup from './components/alert-popup';
import { convertPermissions } from './utils/permission/permissionAdapter';
import {
  REDIRECT_PERMISSION_MESSAGE,
  REDIRECT_PERMISSION_MESSAGE_FOR_SWITCH_COMPANY,
  RELOAD_PERMISSION_MESSAGE,
  TENANT_DELETED_MESSAGE,
  LOGOUT_NOTIFICATION_MESSAGE,
} from './constants/common';
import useForceInit from './hooks/custom-hooks/useForceInit';
import useInstance from './hooks/custom-hooks/useInstance';
import useHasTenant from './hooks/custom-hooks/useHasTenant';
import { axiosInstance } from './services/apiInterceptor';
import { BrandingProvider } from './context/brandingContext';

function App(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authService = new AuthService();
  const token = useSelector(selectToken);
  const showNotify = useSelector(selectNotify);
  const { getTenantById } = useInstance();
  const [isNotify, setNotify] = useState(false);
  const { isLoading, handleRequest } = useWealthlaneIdentityApi();
  const [permissionStatus, setPermissionStatus] = useState<string>('');

  // Inject axios instance into table builder context
  // useAxiosInjection(axiosInstance);
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const {
    value: isInstanceModalOpen,
    setFalse: closeInstanceModal,
    setTrue: openInstanceModal,
  } = useBoolean(false);
  const {
    value: isLogoutModalOpen,
    setFalse: closeLogoutModal,
    setTrue: openLogoutModal,
  } = useBoolean(false);
  const { onKillInstance } = useInstance({
    onClose() {},
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useForceInit();
  useHasTenant();
  const currentHash = window.location.hash;

  // Add effect to listen for mobile menu state changes
  useEffect(() => {
    const handleMobileMenuToggle = (event: CustomEvent) => {
      setIsMobileMenuOpen(event.detail.isOpen);
    };

    window.addEventListener('mobileMenuToggle', handleMobileMenuToggle as EventListener);

    return () => {
      window.removeEventListener('mobileMenuToggle', handleMobileMenuToggle as EventListener);
    };
  }, []);

  useEffect(() => {
    authService
      .getUser()
      .then((user) => {
        if (user != null) {
          dispatch(
            setCredentials({
              isAuthenticated: authService.isAuthenticated(),
              token: authService.getToken(),
              user,
            })
          );
        }
      })
      .catch((err) => {
        if (err == null) void authService.signOutRedirectCallback();
      });
    setNotify(true);
  }, []);

  // Setup listener for logout events from other tabs
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'logout_event') {
        openLogoutModal();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (token?.length > 0) {
      handleRequest(
        AbpApplicationConfigurationService.getApiIdentityApiAbpApplicationConfiguration()
      )
        .then(async (response) => {
          // const newPermissionFeatureSet = await handleRequest(
          //   CompanyFeatureService.getApiIdentityCompanyFeaturePermissions()
          // );
          // const featureSet = await handleRequest(
          //   CompanyFeatureService.getApiIdentityCompanyFeatureSet()
          // );

          // dispatch(setFeatureSetList({ featureSetList: featureSet ?? [] }));
          // const convertedPermissions = convertPermissions(newPermissionFeatureSet ?? []);
          const { nonFieldLevelPermissionsGroups } = getFilteredPermissionData(
            response?.auth?.grantedPolicies
          );
          // const { nonFieldLevelPermissionsGroups } = getFilteredPermissionData(
          //   convertedPermissions,
          //   undefined,
          //   featureSet?.[0]?.name ?? undefined
          // );

          const RouteList = Object.keys(nonFieldLevelPermissionsGroups).map((item) => item);
          // if (response?.auth) {
          //   response.auth.grantedPolicies = convertedPermissions;
          // }

          dispatch(
            setRouteList({
              currentUserAuthList: response ?? {},
              currentUserRoutesList: RouteList ?? [],
            })
          );
        })
        .catch((err) => {
          console.log('err', err);
        });
    }
  }, [token]);

  useEffect(() => {
    document.getElementsByTagName('html')[0].style.overflow = 'unset';
  }, [location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  window.addEventListener('PERMISSION_CHANGED', (evt: any) => {
    setPermissionStatus(evt.detail);
    openModal();
  });

  window.addEventListener('TENANT_DELETED', (evt: any) => {
    openInstanceModal();
  });

  const handleCloseModal = () => {
    closeModal();
    // see ticket number why it was commented #3271
    // if (localStorage?.getItem('isOrigin') === 'false') {
    //   localStorage?.removeItem('isOrigin');
    //   localStorage?.removeItem('tenantId');
    // }
    if (permissionStatus === 'SIGN_IN') {
      navigate(`/check-user${currentHash}`);
    } else if (permissionStatus === 'REFRESH') {
      navigate(`/${currentHash}`);
      navigate(0);
    } else {
      navigate(0);
    }
  };

  const onLogout = () => {
    window.location.reload();
  };

  const isAuthenticated = authService.isAuthenticated() && Boolean(token);

  return (
    <div>
      {isLoading ? (
        <Spinners containerClassName='h-screen' />
      ) : (
        <BrandingProvider enabled={isAuthenticated}>
          <>
            <Navbar isAuth={isAuthenticated} showNotify={showNotify} isNotify={isNotify} />
            <MainContentWrapper
              id='main-content'
              isMenuOpen={isMobileMenuOpen}
              hasNotify={false}
              img={pingBg}>
              <RoutesContainer />
            </MainContentWrapper>
            {isModalOpen && (
              <AlertPopup
                isShow={isModalOpen}
                ConfirmTitle='Ok'
                customTitle={
                  permissionStatus === 'RELOAD'
                    ? RELOAD_PERMISSION_MESSAGE
                    : getTenantById()
                    ? REDIRECT_PERMISSION_MESSAGE_FOR_SWITCH_COMPANY
                    : REDIRECT_PERMISSION_MESSAGE
                }
                handleSubmit={() => handleCloseModal()}
              />
            )}
            {isInstanceModalOpen && (
              <AlertPopup
                isShow={isInstanceModalOpen}
                ConfirmTitle='Go to Main Instance'
                icon={false}
                customTitle={TENANT_DELETED_MESSAGE}
                handleSubmit={() => onKillInstance()}
              />
            )}
            {isLogoutModalOpen && (
              <AlertPopup
                isShow={isLogoutModalOpen}
                ConfirmTitle={'Ok'}
                customTitle={LOGOUT_NOTIFICATION_MESSAGE}
                handleSubmit={onLogout}
              />
            )}
          </>
        </BrandingProvider>
      )}
    </div>
  );
}

export default App;
